import { WindingDir } from "../core";
import {
  polygonUnion as polygonUnionBase,
  polygonIntersection as polygonIntersectionBase,
  polygonSubtract as polygonSubtractBase,
} from "../polygon";
import {
  polyLine2Area,
  polyLine2Close,
  polyLine2Copy,
  polyLine2MergeCoincidentVertices,
  polyLine2MergeCollinearEdges,
  polyLine2Reverse,
  polyLine2RoundInput,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2Unclose,
} from "../polyLine2";
import { polygonArea } from "../polyLine2Polygon";
import type { PolyLine2Polygon, PolyLine2 } from "../polyLine2Types";
import { polyLine2New } from "../polyLine2Types";
import type { DCEL, Polygon, PolygonLoop } from "./dcel";
import { dcelFromPolygons } from "./dcelFromPolygons";
import { buildPolyOpMemoKey, polyOpMemoGet, polyOpMemoSet } from "./polyOpMemo";
import { polyIntersect, polySubtract, polyUnion } from "./setOps";
import type { SetOpsTestCase } from "./test/setOpsTestCaseType";

export const SNAP_ROUNDING_TOLERANCE = 1e-3;
const ROUNDING_COEFFICIENT = 1000;
const FILTERING_AREA_TOL = 1e-3;
const MERGE_COINCIDENT_VERTICES_TOL = 1e-10;

/**
 * Debugging conditionals and storage: uncomment to use
 * DO NOT DELETE
 * */
const CheckAndSaveFailureTestCases: boolean = true;
const StoreMaxFailureTestCases: number = 1; //maximum number of failure test cases to be stored in the console log at once
const FailureTestCases: SetOpsTestCase[] = [];
/** */

export function polyLine2ToPolyClipPolygonLoop(
  pl: PolyLine2,
  round: boolean,
  reverse: boolean,
  windDir: WindingDir = WindingDir.CW,
): PolygonLoop {
  const pl2 = polyLine2Unclose(
    windDir === WindingDir.CW ? polyLine2SetCW(polyLine2Copy(pl)) : polyLine2SetCCW(polyLine2Copy(pl)),
  );
  const a = new Array(pl2.length / 2);
  for (let i = 0, l = pl2.length; i < l; i += 2) {
    if (round) {
      a[i >> 1] = [Math.round(pl2[i] * ROUNDING_COEFFICIENT), Math.round(pl2[i + 1] * ROUNDING_COEFFICIENT)];
    } else {
      a[i >> 1] = [pl2[i], pl2[i + 1]];
    }
  }
  if (reverse) {
    a.reverse();
  }
  return a;
}

export function polygonLoopToPolyLine2(a: PolygonLoop, round: boolean, reverse: boolean): PolyLine2 {
  const pl = polyLine2New((a.length + 1) * 2);

  for (let i = 0, l = a.length; i < l; i++) {
    const pt = a[i];
    if (round) {
      pl[2 * i] = pt[0] / ROUNDING_COEFFICIENT;
      pl[2 * i + 1] = pt[1] / ROUNDING_COEFFICIENT;
    } else {
      pl[2 * i] = pt[0];
      pl[2 * i + 1] = pt[1];
    }
  }
  if (a.length > 0) {
    pl[2 * a.length] = pl[0];
    pl[2 * a.length + 1] = pl[1];
  }
  if (reverse) {
    polyLine2Reverse(pl);
  }
  return pl;
}

export function polyLine2ArrayToPolygon(
  pl: PolyLine2[][],
  round: boolean = false,
  reverse: boolean = false,
): Polygon[] {
  const mainWindingDir = reverse ? WindingDir.CW : WindingDir.CCW;
  const innerWindingDir = reverse ? WindingDir.CCW : WindingDir.CW;
  return pl.map((x) =>
    x.map((y, index) =>
      polyLine2ToPolyClipPolygonLoop(y, round, reverse, index === 0 ? mainWindingDir : innerWindingDir),
    ),
  );
}

export function polygonArrayToPolyLine2Array(pl: Polygon[], round: boolean, reverse: boolean): PolyLine2Polygon[] {
  return pl.map((x) => x.map((y) => polygonLoopToPolyLine2(y, round, reverse)));
}

export function getDCEL(
  pls: PolyLine2Polygon[],
  filterAreaTol: number = FILTERING_AREA_TOL,
  round: boolean = false,
  reverse: boolean = true,
): DCEL | null {
  const filteredPolys = pls.map((x) =>
    x.filter((y) => polyLine2Area(polyLine2Close(polyLine2Copy(y))) > filterAreaTol),
  );
  if (filteredPolys.length === 0) {
    return null;
  }
  const converted = polyLine2ArrayToPolygon(pls, round, reverse);
  return dcelFromPolygons({ polygons: converted });
}

export function polyOpUsingDCEL(dcel: DCEL, operands: number[], op: "union" | "intersect" | "subtract"): Polygon[] {
  let outputPolygons: Polygon[] = [];
  if (op === "intersect") {
    outputPolygons = polyIntersect(dcel, operands);
  } else if (op === "union") {
    outputPolygons = polyUnion(dcel, operands);
  } else if (op === "subtract") {
    outputPolygons = polySubtract(dcel, [operands[0]], [...operands.slice(1)]);
  }
  return outputPolygons;
}

/**DEBUGGING HINTS:
 * The output stored here can be directly copied into a test array in testCasesSetOps.ts and then can be
 * debugged further indivdually by running the test case in the test suite.
 * DO NOT DELETE
 */
export function checkAndSaveFailureTestCasesForDebugging(
  pls: PolyLine2Polygon[], //input polygons
  op: "union" | "intersect" | "subtract", //operation
  outputPolysToValidate: PolyLine2Polygon[],
  areaDiffTolerance: number = 0.1,
  areaPercentTolerance: number = 0.5,
) {
  if (!CheckAndSaveFailureTestCases || FailureTestCases.length === StoreMaxFailureTestCases) {
    return;
  }

  let baseLineOutput: PolyLine2Polygon[] = [];

  if (op === "union") {
    baseLineOutput = polygonUnionBase(pls, true);
  } else if (op === "intersect") {
    baseLineOutput = polygonIntersectionBase(pls, true);
  } else if (op === "subtract") {
    baseLineOutput = polygonSubtractBase(pls, true);
  }

  const outputPolyCount1 = outputPolysToValidate.reduce((count, polygon) => count + polygon.length, 0);
  const outputPolyCount2 = baseLineOutput.reduce((count, polygon) => count + polygon.length, 0);

  if (outputPolyCount1 !== outputPolyCount2) {
    console.log("PolyClip Output Poly Count:", outputPolyCount1, "BaseLine Output Poly Count:", outputPolyCount2);
  }

  const outputPolyPointsCount1 = outputPolysToValidate.reduce(
    (count, polygon) => count + polygon.reduce((count, p) => count + p.length, 0),
    0,
  );
  const outputPolyPointsCount2 = baseLineOutput.reduce(
    (count, polygon) => count + polygon.reduce((count, p) => count + p.length, 0),
    0,
  );

  if (outputPolyPointsCount1 !== outputPolyPointsCount2) {
    console.log(
      "PolyClip Output Poly Points Count:",
      outputPolyPointsCount1,
      "BaseLine Output Poly Points Count:",
      outputPolyPointsCount2,
    );
  }

  const area1 = outputPolysToValidate.reduce((area, polygon) => area + polygonArea(polygon), 0);
  const area2 = baseLineOutput.reduce((area, polygon) => area + polygonArea(polygon), 0);

  if (Math.abs(area1 - area2) / area2 < areaPercentTolerance || Math.abs(area1 - area2) < areaDiffTolerance) {
    return;
  }

  console.log(
    "Area PolyClip:",
    area1,
    "Area BaseLine:",
    area2,
    "Difference:",
    area1 - area2,
    "% Difference:",
    ((area1 - area2) / area2) * 100,
  );

  const pl = polyLine2ArrayToPolygon(pls, false, true);

  const operands: number[] = [];
  for (let i = 0; i < pl.length; i++) {
    operands.push(i);
  }

  if (FailureTestCases.length < StoreMaxFailureTestCases) {
    FailureTestCases.push({
      name: `Test fail case #${FailureTestCases.length + 1}`,
      polygons: pl,
      op,
      operands: op !== "subtract" ? [operands] : operands.map((x) => [x]),
    });
  }
  if (FailureTestCases.length === StoreMaxFailureTestCases) {
    //note that this logic of console display only works if there are more than or equal to StoreMaxFailureTestCases
    console.log("FailureTestCases:", JSON.stringify(FailureTestCases));
  }
}

export enum PolygonRoundingMode {
  NONE,
  ROUND,
  SNAP_ROUND,
}

type PolyOpOptions = {
  pls: PolyLine2Polygon[];
  op: "union" | "intersect" | "subtract";
  roundingMode?: PolygonRoundingMode;
  reverse?: boolean;
  mergeCoincidentVertexTol?: number;
  mergeCollinear?: boolean;
  snapRoundingTolerance?: number;
};

export function polyOp(options: PolyOpOptions): PolyLine2Polygon[] {
  const {
    pls,
    op,
    roundingMode = PolygonRoundingMode.ROUND,
    reverse = true,
    mergeCoincidentVertexTol = MERGE_COINCIDENT_VERTICES_TOL,
    mergeCollinear = true,
    snapRoundingTolerance = SNAP_ROUNDING_TOLERANCE,
  } = options;

  const memoKey = buildPolyOpMemoKey(
    pls,
    op,
    roundingMode,
    reverse,
    mergeCoincidentVertexTol,
    mergeCollinear,
    snapRoundingTolerance,
  );
  const memoized = polyOpMemoGet(memoKey);
  if (memoized !== undefined) {
    return memoized;
  }

  const result = polyOpUncached(
    pls,
    op,
    roundingMode,
    reverse,
    mergeCoincidentVertexTol,
    mergeCollinear,
    snapRoundingTolerance,
  );
  polyOpMemoSet(memoKey, result);
  return result;
}

function polyOpUncached(
  pls: PolyLine2Polygon[],
  op: "union" | "intersect" | "subtract",
  roundingMode: PolygonRoundingMode,
  reverse: boolean,
  mergeCoincidentVertexTol: number,
  mergeCollinear: boolean,
  snapRoundingTolerance: number,
): PolyLine2Polygon[] {
  const round = roundingMode === PolygonRoundingMode.ROUND;
  const converted = polyLine2ArrayToPolygon(pls, round, reverse);
  const dcel = dcelFromPolygons({
    polygons: converted,
    snapRoundPrecision: roundingMode === PolygonRoundingMode.SNAP_ROUND ? snapRoundingTolerance : undefined,
  });
  const operands: number[] = [];
  for (let i = 0; i < converted.length; i++) {
    operands.push(i);
  }
  let outputPolygons: Polygon[] = [];
  if (op === "intersect") {
    outputPolygons = polyIntersect(dcel, operands);
  } else if (op === "union") {
    outputPolygons = polyUnion(dcel, operands);
  } else if (op === "subtract") {
    outputPolygons = polySubtract(dcel, [operands[0]], [...operands.slice(1)]);
  }
  return preprocessInputPolys(
    polygonArrayToPolyLine2Array(outputPolygons, round, reverse),
    mergeCoincidentVertexTol,
    roundingMode,
    mergeCollinear,
  );
}

interface PolyFiltered {
  polys: PolyLine2Polygon[];
  firstPolyFiltered: boolean;
}

export function filterPolysUsingAreaTol(
  pls: PolyLine2Polygon[],
  filterAreaTol: number = FILTERING_AREA_TOL,
): PolyFiltered {
  let firstPolyFiltered: boolean = false;
  const filteredPolys = pls
    .map((x, index) =>
      x.filter((y) => {
        const polyAreaGreaterThanFilterTol = polyLine2Area(polyLine2Close(polyLine2Copy(y))) > filterAreaTol;
        if (!polyAreaGreaterThanFilterTol && index === 0) {
          firstPolyFiltered = true;
        }
        return polyAreaGreaterThanFilterTol;
      }),
    )
    .filter((x) => x.length > 0);

  return { polys: filteredPolys, firstPolyFiltered };
}

export function preprocessInputPolys(
  pls: PolyLine2Polygon[],
  mergeCoincidentVertexTol: number,
  roundingMode: PolygonRoundingMode = PolygonRoundingMode.ROUND,
  mergeCollinear: boolean = true,
): PolyLine2Polygon[] {
  const round = roundingMode === PolygonRoundingMode.ROUND;
  return pls.map((x) =>
    x.map((y) => {
      let output = mergeCollinear
        ? polyLine2MergeCollinearEdges(polyLine2MergeCoincidentVertices(polyLine2Copy(y), mergeCoincidentVertexTol))
        : polyLine2MergeCoincidentVertices(polyLine2Copy(y), mergeCoincidentVertexTol);
      if (round) {
        output = polyLine2RoundInput(output, ROUNDING_COEFFICIENT);
      }
      return output;
    }),
  );
}

type PolygonUnionOptions = {
  pls: PolyLine2Polygon[];
  filterAreaTol?: number;
  roundingMode?: PolygonRoundingMode;
  reverse?: boolean;
  mergeCoincidentVertexTol?: number;
  mergeCollinear?: boolean;
  snapRoundingTolerance?: number;
};

export function polygonUnion(options: PolygonUnionOptions): PolyLine2Polygon[] {
  const {
    pls,
    filterAreaTol = FILTERING_AREA_TOL,
    roundingMode = PolygonRoundingMode.ROUND,
    reverse = true,
    mergeCoincidentVertexTol = MERGE_COINCIDENT_VERTICES_TOL,
    mergeCollinear = true,
    snapRoundingTolerance = SNAP_ROUNDING_TOLERANCE,
  } = options;
  const filteredPolys: PolyFiltered = filterPolysUsingAreaTol(pls, filterAreaTol);

  if (filteredPolys.polys.length === 0 || (filteredPolys.polys.length === 1 && filteredPolys.polys[0].length === 1)) {
    return preprocessInputPolys(filteredPolys.polys, mergeCoincidentVertexTol, roundingMode, mergeCollinear);
  }
  const union = polyOp({
    pls: filteredPolys.polys,
    op: "union",
    roundingMode,
    reverse,
    mergeCoincidentVertexTol,
    mergeCollinear,
    snapRoundingTolerance,
  });

  return union;
}

type PolygonIntersectionOptions = {
  pls: PolyLine2Polygon[];
  filterAreaTol?: number;
  roundingMode?: PolygonRoundingMode;
  reverse?: boolean;
  mergeCoincidentVertexTol?: number;
  mergeCollinear?: boolean;
  snapRoundingTolerance?: number;
};

export function polygonIntersection(options: PolygonIntersectionOptions): PolyLine2Polygon[] {
  const {
    pls,
    filterAreaTol = FILTERING_AREA_TOL,
    roundingMode = PolygonRoundingMode.ROUND,
    reverse = true,
    mergeCoincidentVertexTol = MERGE_COINCIDENT_VERTICES_TOL,
    mergeCollinear = true,
    snapRoundingTolerance = SNAP_ROUNDING_TOLERANCE,
  } = options;
  const filteredPolys = filterPolysUsingAreaTol(pls, filterAreaTol);

  if (filteredPolys.polys.length === 0 || (filteredPolys.polys.length === 1 && filteredPolys.polys[0].length === 1)) {
    return preprocessInputPolys(filteredPolys.polys, mergeCoincidentVertexTol, roundingMode, mergeCollinear);
  }
  const intersected = polyOp({
    pls: filteredPolys.polys,
    op: "intersect",
    roundingMode,
    reverse,
    mergeCoincidentVertexTol,
    mergeCollinear,
    snapRoundingTolerance,
  });

  return intersected;
}

type PolygonSubtractOptions = {
  pls: PolyLine2Polygon[];
  filterAreaTol?: number;
  roundingMode?: PolygonRoundingMode;
  reverse?: boolean;
  mergeCoincidentVertexTol?: number;
  mergeCollinear?: boolean;
  snapRoundingTolerance?: number;
};

export function polygonSubtract(options: PolygonSubtractOptions): PolyLine2Polygon[] {
  const {
    pls,
    filterAreaTol = FILTERING_AREA_TOL,
    roundingMode = PolygonRoundingMode.ROUND,
    reverse = true,
    mergeCoincidentVertexTol = MERGE_COINCIDENT_VERTICES_TOL,
    mergeCollinear = true,
    snapRoundingTolerance = SNAP_ROUNDING_TOLERANCE,
  } = options;
  const filteredPolys = filterPolysUsingAreaTol(pls, filterAreaTol);

  if (filteredPolys.firstPolyFiltered) {
    return [];
  }

  if (filteredPolys.polys.length === 0 || (filteredPolys.polys.length === 1 && filteredPolys.polys[0].length === 1)) {
    return preprocessInputPolys(filteredPolys.polys, mergeCoincidentVertexTol, roundingMode, mergeCollinear);
  }

  const subtracted = polyOp({
    pls: filteredPolys.polys,
    op: "subtract",
    roundingMode,
    reverse,
    mergeCoincidentVertexTol,
    mergeCollinear,
    snapRoundingTolerance,
  });

  return subtracted;
}
